'use client';

import { c as _c } from "react/compiler-runtime";
import { AdvancedMarker, Map, Marker, useMap } from '@vis.gl/react-google-maps';
import { useDotEnv } from '@repo/dot-env/client';
import { cn } from '@repo/ui/lib/utils';
import { useEffect } from 'react';
export type LocationPoint = {
  lat: number;
  lng: number;
};
type LocationPickerMapProps = {
  className?: string;
  defaultCenter?: LocationPoint;
  defaultZoom?: number;
  disabled?: boolean;
  mapId?: string;
  onChange?: (point: LocationPoint) => void;
  value?: LocationPoint | null;
};
const DEFAULT_CENTER = {
  lat: 54,
  lng: -2
};
const DEFAULT_ZOOM = 6;
const SELECTED_ZOOM = 15;
function MapController(t0) {
  const $ = _c(4);
  const {
    value
  } = t0;
  const map = useMap();
  let t1;
  let t2;
  if ($[0] !== map || $[1] !== value) {
    t1 = () => {
      if (value && map) {
        map.panTo(value);
        if (map.getZoom() !== SELECTED_ZOOM) {
          map.setZoom(SELECTED_ZOOM);
        }
      }
    };
    t2 = [value, map];
    $[0] = map;
    $[1] = value;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
}
export const LocationPickerMap = t0 => {
  const $ = _c(22);
  const {
    className,
    defaultCenter: t1,
    defaultZoom: t2,
    disabled: t3,
    mapId: mapIdProp,
    onChange,
    value
  } = t0;
  const defaultCenter = t1 === undefined ? DEFAULT_CENTER : t1;
  const defaultZoom = t2 === undefined ? DEFAULT_ZOOM : t2;
  const disabled = t3 === undefined ? false : t3;
  const env = useDotEnv();
  const resolvedMapId = mapIdProp ?? env.GOOGLE_MAP_ID;
  let t4;
  if ($[0] !== onChange) {
    t4 = latLng => {
      if (!latLng) {
        return;
      }
      onChange?.({
        lat: latLng.lat(),
        lng: latLng.lng()
      });
    };
    $[0] = onChange;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const handleDragEnd = t4;
  let t5;
  if ($[2] !== className) {
    t5 = cn("size-full", className);
    $[2] = className;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const t6 = value ?? defaultCenter;
  const t7 = value ? SELECTED_ZOOM : defaultZoom;
  let t8;
  if ($[4] !== disabled || $[5] !== onChange) {
    t8 = event => {
      if (disabled || !event.detail.latLng) {
        return;
      }
      onChange?.(event.detail.latLng);
    };
    $[4] = disabled;
    $[5] = onChange;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== value) {
    t9 = <MapController value={value} data-sentry-element="MapController" data-sentry-source-file="location-picker-map.tsx" />;
    $[7] = value;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== disabled || $[10] !== handleDragEnd || $[11] !== resolvedMapId || $[12] !== value) {
    t10 = <MapMarker disabled={disabled} mapId={resolvedMapId} onDragEnd={handleDragEnd} value={value} data-sentry-element="MapMarker" data-sentry-source-file="location-picker-map.tsx" />;
    $[9] = disabled;
    $[10] = handleDragEnd;
    $[11] = resolvedMapId;
    $[12] = value;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== resolvedMapId || $[15] !== t10 || $[16] !== t5 || $[17] !== t6 || $[18] !== t7 || $[19] !== t8 || $[20] !== t9) {
    t11 = <Map className={t5} defaultCenter={t6} defaultZoom={t7} mapId={resolvedMapId} onClick={t8} streetViewControl={false} data-sentry-element="Map" data-sentry-component="LocationPickerMap" data-sentry-source-file="location-picker-map.tsx">{t9}{t10}</Map>;
    $[14] = resolvedMapId;
    $[15] = t10;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  return t11;
};
type MapMarkerProps = {
  disabled: boolean;
  mapId: string | undefined;
  onDragEnd: (latLng: null | undefined | {
    lat(): number;
    lng(): number;
  }) => void;
  value: LocationPoint | null | undefined;
};
function MapMarker(t0) {
  const $ = _c(12);
  const {
    disabled,
    mapId,
    onDragEnd,
    value
  } = t0;
  if (!value) {
    return null;
  }
  if (mapId) {
    const t1 = !disabled;
    let t2;
    if ($[0] !== onDragEnd) {
      t2 = event => onDragEnd(event.latLng);
      $[0] = onDragEnd;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== t1 || $[3] !== t2 || $[4] !== value) {
      t3 = <AdvancedMarker draggable={t1} onDragEnd={t2} position={value} />;
      $[2] = t1;
      $[3] = t2;
      $[4] = value;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  const t1 = !disabled;
  let t2;
  if ($[6] !== onDragEnd) {
    t2 = event_0 => onDragEnd(event_0.latLng);
    $[6] = onDragEnd;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== value) {
    t3 = <Marker draggable={t1} onDragEnd={t2} position={value} data-sentry-element="Marker" data-sentry-component="MapMarker" data-sentry-source-file="location-picker-map.tsx" />;
    $[8] = t1;
    $[9] = t2;
    $[10] = value;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}